import React, { useState } from 'react';
import './Gallery.css';
import { Helmet } from '@dr.pogodin/react-helmet';
import { Link } from 'react-router-dom';
import Button from '@mui/material/Button';

interface GalleryProps {
  images: string[];
}

const Gallery: React.FC<GalleryProps> = ({ images }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const clickSound = new Audio('/assets/click.mp3');

  const changeImage = (direction: number) => {
    clickSound.play();
    setCurrentImageIndex((currentImageIndex + direction + images.length) % images.length);
  };

  const handleImageClick = (event: React.MouseEvent<HTMLImageElement>) => {
    const imageWidth = event.currentTarget.offsetWidth;
    const clickX = event.nativeEvent.offsetX;

    if (clickX < imageWidth / 2) {
      if (currentImageIndex > 0) {
        clickSound.play();
        changeImage(-1);
      }
    } else {
      if (currentImageIndex < images.length - 1) {
        clickSound.play();
        changeImage(1);
      }
    }
  };

  return (
    <>
      <Helmet>
        <title>Gallery | Atomic Tattoo Removal</title>
        <meta
          name="description"
          content="Explore our gallery of before-and-after tattoo removal photos. See the amazing results achieved with Atomic Tattoo Removal's advanced laser technology."
        />
        <meta
          name="keywords"
          content="tattoo removal gallery, before and after tattoo removal, laser tattoo removal results, Atomic Tattoo Removal photos"
        />
        <meta property="og:title" content="Gallery | Atomic Tattoo Removal" />
        <meta
          property="og:description"
          content="Explore our gallery of before-and-after tattoo removal photos. See the amazing results achieved with Atomic Tattoo Removal's advanced laser technology."
        />
        <meta
          property="og:image"
          content="https://www.atomictattooremoval.com/assets/tattooremoval.jpeg"
        />
        <meta property="og:url" content="https://www.atomictattooremoval.com/before-after-images" />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">
          {`
          {
            "@context": "https://schema.org",
            "@type": "ImageGallery",
            "name": "Before and After Tattoo Removal Gallery",
            "description": "Explore our gallery of before-and-after tattoo removal photos showcasing the effectiveness of our laser technology.",
            "image": [
              ${images.map((image) => `"https://www.atomictattooremoval.com${image}"`).join(",")}
            ]
          }
          `}
        </script>        
      </Helmet>
      <div style={{ textAlign: 'center', marginBottom: '20px', padding: '20px' }}>
        <h1 style={{ fontSize: '1.5rem', marginBottom: '10px' }}>
          See the Power of the Trinity Laser in Action!
        </h1>
        <p style={{ fontSize: '1rem', color: '#555', lineHeight: '1.6', maxWidth: '800px', margin: '0 auto' }}>
          All the incredible before-and-after photos you see here were achieved using the cutting-edge{' '}
          <strong>Trinity Laser</strong>. This advanced laser technology is designed to target the{' '}
          <strong>full spectrum of tattoo colors</strong>, from stubborn black ink to vibrant reds, blues, and greens.
          Whether you're looking to fade a tattoo for a cover-up or remove it entirely, the Trinity Laser delivers safe,
          effective, and precise results.
        </p>
      </div>
      <div
        style={{
          backgroundColor: '#5C96AC', // Blue background
          padding: '20px',
          borderRadius: '10px',
          margin: '20px auto',
          maxWidth: '900px',
        }}
      >
        <div className="gallery">
          {currentImageIndex > 0 && (
            <button className="prev" onClick={() => changeImage(-1)}>
              &#10094;
            </button>
          )}
          {images.map((src, index) => {
            // WebP sibling generated by optimize-images.mjs; original is the fallback.
            const webp = src.replace(/\.(png|jpe?g)$/i, '.webp');
            return (
              <picture
                key={index}
                style={{ display: index === currentImageIndex ? 'block' : 'none', width: '100%' }}
              >
                <source srcSet={encodeURI(webp)} type="image/webp" />
                <img
                  src={src}
                  loading="lazy"
                  decoding="async"
                  alt={
                    src.includes('before')
                      ? 'Before and after tattoo removal'
                      : 'See more tattoo removal results on our Instagram @atomic_tattoo_removal'
                  }
                  onClick={handleImageClick}
                />
              </picture>
            );
          })}
          {currentImageIndex < images.length - 1 && (
            <button className="next" onClick={() => changeImage(1)}>
              &#10095;
            </button>
          )}
        </div>
      </div>
      <div style={{ textAlign: 'center', marginTop: '30px', padding: '20px' }}>
        <h3 style={{ fontSize: '1.5rem', marginBottom: '20px' }}>
          Ready to Start Your Tattoo Removal Journey?
        </h3>
        <Link to="/book-consultation" style={{ textDecoration: 'none' }}>           
        <Button color="secondary"
          variant="contained"
          onClick={() => {
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
              event: 'book_now_click',
              button_location: 'gallery_page',
            });
          }}
          sx={{ 
            color: '#f0e9d4', 
            '&:hover': { color:'#ffffff',backgroundColor:'#cb4f2e' } }}
          >
          👉 Book Your Free Consultation
        </Button>  
      </Link>  
      </div>
    </>
  );
};

export default Gallery;